import { useEffect, useRef, useState } from 'react'
import { Dialog } from './Dialog'
import type { CardCopy } from '../lib/hubCopy'
import { clampCrop, initialCrop, type Crop } from '../lib/profilePhoto'
import {
  cropBoxIn,
  maxZoomIn,
  panCropIn,
  zoomCropIn,
  type Frame,
} from '../lib/photoCrop'

/**
 * Crop and zoom the card photo before it is saved. David, 6 Oct: "we need a
 * modal to crop and zoom on the photo." 132 took the centre square without
 * asking, which is the face in a selfie and somebody's shoulder in anything
 * else.
 *
 * ── WHAT YOU SEE IS WHAT IS SAVED ───────────────────────────────────────────
 *
 * The frame is a square with the card's CIRCLE marked inside it, because the
 * card draws a circle — so what sits inside the ring is what a client will
 * see. The saved file is the whole square (cropRect), so nothing outside the
 * ring is lost if the card's shape ever changes.
 *
 * ── FOUR WAYS TO MOVE IT, BECAUSE FOUR KINDS OF HAND ────────────────────────
 *
 *   drag      one pointer — finger, mouse or pen (pointer events, captured).
 *   pinch     two fingers, zooming about the point between them.
 *   wheel     a mouse or trackpad, about the cursor. A NATIVE listener with
 *             passive:false, because React's onWheel is passive and could not
 *             stop the panel scrolling under it.
 *   keyboard  the frame takes focus: arrows move, + and − zoom. And the
 *             slider is a real range input, so zoom is reachable and announced
 *             without any of the above.
 *
 * All of them go through profilePhoto.ts's pure functions, which keep the
 * square inside the photo — there is never grey in the frame to save.
 *
 * ── A SIBLING OF THE CARD DIALOG, NOT A CHILD (read before moving it) ──────
 *
 * Dialog portals to <body>, but React events still bubble through the REACT
 * tree. Rendered inside the card Dialog, a shift-Tab here would bubble into
 * the card's own Tab trap, which sees focus outside its panel and pulls it
 * back there — the crop dialog would lose focus to the one beneath it. So
 * BusinessCardDialog renders this NEXT TO its Dialog. useSurfaceLayer makes
 * this one topmost, so Escape and the scrim close the crop, not the card.
 */

/** The frame's edge in CSS pixels. Fits a 320px phone inside the dialog's
 *  padding with room to spare; the canvas behind it is drawn at devicePixelRatio. */
export const CROP_VIEW = 264

export function PhotoCropDialog({
  source,
  busy,
  error,
  onCancel,
  onUse,
  t,
}: {
  /** The decoded photo, or null when the dialog is shut. */
  source: ImageBitmap | null
  busy: boolean
  error: string | null
  onCancel: () => void
  onUse: (crop: Crop) => void
  t: CropCopy
}) {
  const w = source?.width ?? 1
  const h = source?.height ?? 1
  // The crop is kept WITH the photo it belongs to, so a new photo starts
  // centred at zoom 1 on its very first frame — a reset in an effect would
  // draw one frame of the new photo at the old photo's coordinates first.
  const [held, setHeld] = useState<{ src: ImageBitmap | null; crop: Crop }>({ src: null, crop: initialCrop(1, 1) })
  const crop = held.src === source ? held.crop : initialCrop(w, h)
  function setCrop(next: (c: Crop) => Crop) {
    setHeld((s) => ({ src: source, crop: next(s.src === source ? s.crop : initialCrop(w, h)) }))
  }
  return (
    <Dialog open={source !== null} title={t.cropTitle} onClose={busy ? () => undefined : onCancel} className="croppanel">
      <div className="cropsheet">
        {source && (
          <CropCanvas
            source={source}
            frame={SQUARE}
            crop={crop}
            setCrop={setCrop}
            busy={busy}
            label={t.cropFrameLabel}
            ring
          />
        )}

        <p className="cropsheet__help">{t.cropHelp}</p>

        <CropZoom frame={SQUARE} w={w} h={h} crop={crop} setCrop={setCrop} busy={busy} label={t.zoomLabel} />

        {error && <p className="profile__error" role="alert">{error}</p>}

        <div className="cropsheet__btns">
          <button type="button" className="btn btn--primary" disabled={busy} onClick={() => onUse(clampCrop(CROP_VIEW, w, h, crop))}>
            {busy ? t.photoSaving : t.usePhoto}
          </button>
          <button type="button" className="btn btn--quiet" disabled={busy} onClick={onCancel}>
            {t.cancel}
          </button>
        </div>
      </div>
    </Dialog>
  )
}

/** The card photo's frame: a square with the card's circle in it. */
const SQUARE: Frame = { w: CROP_VIEW, h: CROP_VIEW }

/** The strings the crop dialog needs. The card passes its CardCopy; the
 *  composer passes its own (FED-66). */
export type CropCopy = Pick<CardCopy, 'cropTitle' | 'cropFrameLabel' | 'cropHelp' | 'zoomLabel' | 'photoSaving' | 'usePhoto' | 'cancel'>

/**
 * The frame and the four ways to move the photo in it (FED-66 lifted this out
 * of the card's dialog so the feed's crop is the same widget, not a copy).
 * Any rectangle: the frame's shape IS the crop's shape. Controlled — the
 * crop lives with the caller, which knows which photo and shape it belongs to.
 */
export function CropCanvas({
  source,
  frame,
  crop,
  setCrop,
  busy,
  label,
  ring = false,
}: {
  source: ImageBitmap
  frame: Frame
  crop: Crop
  setCrop: (next: (c: Crop) => Crop) => void
  busy: boolean
  label: string
  /** The card's circle over the frame. The feed has no circle. */
  ring?: boolean
}) {
  const w = source.width
  const h = source.height
  const canvas = useRef<HTMLCanvasElement>(null)
  const pointers = useRef(new Map<number, { x: number; y: number }>())

  // Draw. Effects do not run under renderToString, so the suite sees an empty
  // canvas and the markup around it — the arithmetic is proven separately.
  useEffect(() => {
    const el = canvas.current
    if (!el) return
    const dpr = Math.min(window.devicePixelRatio || 1, 3)
    const pw = Math.round(frame.w * dpr)
    const ph = Math.round(frame.h * dpr)
    if (el.width !== pw || el.height !== ph) {
      el.width = pw
      el.height = ph
    }
    const ctx = el.getContext('2d')
    if (!ctx) return
    const r = cropBoxIn(frame, w, h, crop)
    ctx.imageSmoothingQuality = 'high'
    ctx.clearRect(0, 0, pw, ph)
    ctx.drawImage(source, r.sx, r.sy, r.sw, r.sh, 0, 0, pw, ph)
  }, [source, crop, w, h, frame])

  // The wheel, natively, so preventDefault is allowed (see the header).
  useEffect(() => {
    const el = canvas.current
    if (!el) return
    function onWheel(e: WheelEvent) {
      e.preventDefault()
      const box = el!.getBoundingClientRect()
      const ax = e.clientX - box.left - box.width / 2
      const ay = e.clientY - box.top - box.height / 2
      // Exponential, so a trackpad's many small deltas and a mouse's few big
      // ones both feel like the same zoom.
      const factor = Math.exp(-e.deltaY * 0.002)
      setCrop((c) => zoomCropIn(frame, w, h, c, c.zoom * factor, ax, ay))
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
    // setCrop is in the list: the caller's knows which photo and shape the
    // crop belongs to, and a stale one would write to the previous shape.
  }, [source, w, h, frame, setCrop])

  function onPointerDown(e: React.PointerEvent<HTMLCanvasElement>) {
    if (busy) return
    e.currentTarget.setPointerCapture(e.pointerId)
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
  }

  function onPointerMove(e: React.PointerEvent<HTMLCanvasElement>) {
    const map = pointers.current
    const prev = map.get(e.pointerId)
    if (!prev) return
    const next = { x: e.clientX, y: e.clientY }

    if (map.size === 1) {
      map.set(e.pointerId, next)
      setCrop((c) => panCropIn(frame, w, h, c, next.x - prev.x, next.y - prev.y))
      return
    }

    // Pinch: the other finger is wherever it last was. Zoom by the change in
    // distance, about the midpoint, and pan by the midpoint's own movement —
    // so two fingers moving together drag, and spreading them zooms.
    const other = [...map.entries()].find(([id]) => id !== e.pointerId)?.[1]
    map.set(e.pointerId, next)
    if (!other) return
    const d0 = Math.hypot(prev.x - other.x, prev.y - other.y)
    const d1 = Math.hypot(next.x - other.x, next.y - other.y)
    if (d0 < 1) return
    const box = e.currentTarget.getBoundingClientRect()
    const mx = (next.x + other.x) / 2 - box.left - box.width / 2
    const my = (next.y + other.y) / 2 - box.top - box.height / 2
    const pmx = (prev.x + other.x) / 2 - box.left - box.width / 2
    const pmy = (prev.y + other.y) / 2 - box.top - box.height / 2
    setCrop((c) => panCropIn(frame, w, h, zoomCropIn(frame, w, h, c, c.zoom * (d1 / d0), mx, my), mx - pmx, my - pmy))
  }

  function onPointerEnd(e: React.PointerEvent<HTMLCanvasElement>) {
    pointers.current.delete(e.pointerId)
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLCanvasElement>) {
    if (busy) return
    const step = e.shiftKey ? 40 : 10
    const moves: Record<string, [number, number]> = {
      // The photo moves the way the arrow points, like dragging it.
      ArrowLeft: [-step, 0],
      ArrowRight: [step, 0],
      ArrowUp: [0, -step],
      ArrowDown: [0, step],
    }
    if (moves[e.key]) {
      e.preventDefault()
      const [dx, dy] = moves[e.key]
      setCrop((c) => panCropIn(frame, w, h, c, dx, dy))
    } else if (e.key === '+' || e.key === '=') {
      e.preventDefault()
      setCrop((c) => zoomCropIn(frame, w, h, c, c.zoom * 1.1))
    } else if (e.key === '-' || e.key === '_') {
      e.preventDefault()
      setCrop((c) => zoomCropIn(frame, w, h, c, c.zoom / 1.1))
    }
  }

  return (
    <div className="cropframe" style={{ inlineSize: frame.w, blockSize: frame.h }}>
      <canvas
        ref={canvas}
        className="cropframe__canvas"
        width={frame.w}
        height={frame.h}
        tabIndex={0}
        // A widget with its own keys (arrows, + and -), so application
        // rather than img: an img role tells a screen reader not to send
        // the keys at all.
        role="application"
        aria-label={label}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerEnd}
        onPointerCancel={onPointerEnd}
        onKeyDown={onKeyDown}
      />
      {/* The card's circle. Decoration over the canvas, never a target. */}
      {ring && <span className="cropframe__ring" aria-hidden="true" />}
    </div>
  )
}

/** The zoom slider, shared for the same reason. */
export function CropZoom({
  frame,
  w,
  h,
  crop,
  setCrop,
  busy,
  label,
}: {
  frame: Frame
  w: number
  h: number
  crop: Crop
  setCrop: (next: (c: Crop) => Crop) => void
  busy: boolean
  label: string
}) {
  const top = maxZoomIn(frame, w, h)
  return (
    <label className="cropsheet__zoom">
      <span className="cropsheet__zoomlabel">{label}</span>
      <input
        type="range"
        min={1}
        max={top}
        step={0.01}
        value={crop.zoom}
        disabled={busy || top <= 1}
        aria-valuetext={`${Math.round(crop.zoom * 100)}%`}
        onChange={(e) => setCrop((c) => zoomCropIn(frame, w, h, c, Number(e.target.value)))}
      />
    </label>
  )
}
